import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CodeEditor } from '@/components/ui/code-editor';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { authenticateShare, getSharedRequest } from '@/lib/api';
import type {
  DebugRequestForward,
  DebugRequestForwardDetail,
  SharedRequestFullData,
} from '@/types/debug-endpoints';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  Copy,
  Loader2,
  Lock,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

interface SharedRequestViewProps {
  token: string;
}

const formatIsoDate = (isoString?: string | null) => {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const getForwardStatusInfo = (forward: DebugRequestForward) => {
  if (!forward.attempted_at) {
    return {
      label: 'Pending',
      variant: 'secondary' as const,
      color: 'text-yellow-600',
    };
  }

  if (forward.error_message) {
    return {
      label: 'Failed',
      variant: 'destructive' as const,
      color: 'text-red-600',
    };
  }

  if (forward.status_code) {
    const isSuccess = forward.status_code >= 200 && forward.status_code < 300;
    return {
      label: `${forward.status_code}`,
      variant: isSuccess ? ('default' as const) : ('destructive' as const),
      color: isSuccess ? 'text-green-600' : 'text-red-600',
    };
  }

  return {
    label: 'Unknown',
    variant: 'secondary' as const,
    color: 'text-muted-foreground',
  };
};

const ForwardRow = ({ forward }: { forward: DebugRequestForwardDetail }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const statusInfo = getForwardStatusInfo(forward);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  return (
    <>
      <TableRow
        className="cursor-pointer hover:bg-muted/50"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <TableCell className="w-8">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </TableCell>
        <TableCell>
          <p className="font-mono text-xs break-all max-w-md">
            {forward.target_url}
          </p>
        </TableCell>
        <TableCell>
          <Badge variant="outline">{forward.method}</Badge>
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {formatIsoDate(forward.attempted_at)}
        </TableCell>
        <TableCell>
          <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {forward.duration_ms ? `${forward.duration_ms}ms` : '—'}
        </TableCell>
      </TableRow>
      {isExpanded && (
        <TableRow className="bg-muted/30">
          <TableCell colSpan={6} className="p-4">
            <div className="space-y-6">
              {/* Request Details */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold">Request</h3>
                {forward.request_headers &&
                  Object.keys(forward.request_headers).length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-muted-foreground">
                          Headers
                        </Label>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(
                              JSON.stringify(forward.request_headers, null, 2)
                            );
                          }}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                      <CodeEditor
                        value={JSON.stringify(forward.request_headers, null, 2)}
                        language="json"
                        readOnly
                        height="150px"
                      />
                    </div>
                  )}
                {forward.request_body && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-muted-foreground">Body</Label>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(forward.request_body || '');
                        }}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                    <CodeEditor
                      value={forward.request_body}
                      language="json"
                      readOnly
                      height="200px"
                    />
                  </div>
                )}
              </div>

              {/* Response Details */}
              <div className="space-y-4 border-t pt-4">
                <h3 className="text-sm font-semibold">Response</h3>
                {forward.error_message ? (
                  <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                    <p className="font-medium">Error</p>
                    <pre className="mt-1 whitespace-pre-wrap">
                      {JSON.stringify(forward.error_message, null, 2)}
                    </pre>
                  </div>
                ) : (
                  <>
                    {forward.response_headers &&
                      Object.keys(forward.response_headers).length > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs text-muted-foreground">
                              Headers
                            </Label>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(
                                  JSON.stringify(
                                    forward.response_headers,
                                    null,
                                    2
                                  )
                                );
                              }}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                          <CodeEditor
                            value={JSON.stringify(
                              forward.response_headers,
                              null,
                              2
                            )}
                            language="json"
                            readOnly
                            height="150px"
                          />
                        </div>
                      )}
                    {forward.response_body && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-muted-foreground">
                            Body
                          </Label>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(forward.response_body || '');
                            }}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                        <CodeEditor
                          value={forward.response_body}
                          language="json"
                          readOnly
                          height="200px"
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const SharedRequestView = ({ token }: SharedRequestViewProps) => {
  const [password, setPassword] = useState('');
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [sharedData, setSharedData] = useState<SharedRequestFullData | null>(null);
  const [forwards, setForwards] = useState<DebugRequestForwardDetail[]>([]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['shared-request', token],
    queryFn: async () => {
      const result = await getSharedRequest(token!);
      if ('protected' in result && result.protected) {
        setIsPasswordDialogOpen(true);
        return null;
      }
      return result as SharedRequestFullData;
    },
    enabled: !!token,
    retry: false,
  });

  useEffect(() => {
    if (data && 'debug_request' in data) {
      setSharedData(data);
      setForwards(data.forwards || []);
    }
  }, [data]);

  const handlePasswordSubmit = async () => {
    if (!token || !password) return;

    setIsAuthenticating(true);
    try {
      const result = await authenticateShare(token, password);
      setSharedData(result);
      setForwards(result.forwards || []);
      setIsPasswordDialogOpen(false);
      toast.success('Access granted!');
    } catch (err) {
      console.error('Authentication failed:', err);
      toast.error('Invalid password. Please try again.');
    } finally {
      setIsAuthenticating(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to load shared request';
    const isExpired = errorMessage.includes('expired');
    const isNotFound = errorMessage.includes('not found') || errorMessage.includes('404');

    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <CardTitle>
                {isExpired ? 'Share Expired' : isNotFound ? 'Share Not Found' : 'Error'}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {isExpired
                ? 'This share link has expired and is no longer accessible.'
                : isNotFound
                ? 'This share link does not exist or has been revoked.'
                : errorMessage}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      {sharedData && (() => {
        const request = sharedData.debug_request;
        return (
      <div className="container max-w-6xl mx-auto p-4 space-y-4 pb-24">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Shared Request</h1>
            <p className="text-sm text-muted-foreground">
              View-only access to this debug request
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>Expires {formatIsoDate(sharedData.expires_at)}</span>
          </div>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="request" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="request">Request</TabsTrigger>
            {sharedData.include_forwards && (
              <TabsTrigger value="forwards">
                Forwards ({forwards.length})
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="request" className="space-y-4">
            {/* Request Details */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Request Details</CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{request.method}</Badge>
                    <span className="text-xs text-muted-foreground">
                      {formatIsoDate(request.created_at)}
                    </span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* URL */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-medium text-muted-foreground">URL</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(request.url)}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  <p className="font-mono text-sm bg-muted p-2 rounded-md break-all">
                    {request.url}
                  </p>
                </div>

                {/* Headers */}
                {Object.keys(request.headers || {}).length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm font-medium text-muted-foreground">Headers</Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          copyToClipboard(JSON.stringify(request.headers, null, 2))
                        }
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                    <CodeEditor
                      value={JSON.stringify(request.headers, null, 2)}
                      language="json"
                      readOnly
                      height="200px"
                    />
                  </div>
                )}

                {/* Query Parameters */}
                {Object.keys(request.query_params || {}).length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm font-medium text-muted-foreground">
                        Query Parameters
                      </Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          copyToClipboard(JSON.stringify(request.query_params, null, 2))
                        }
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                    <CodeEditor
                      value={JSON.stringify(request.query_params, null, 2)}
                      language="json"
                      readOnly
                      height="150px"
                    />
                  </div>
                )}

                {/* Body */}
                {request.body && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm font-medium text-muted-foreground">
                        Request Body
                      </Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(request.body || '')}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                    <CodeEditor
                      value={request.body}
                      language="json"
                      readOnly
                      height="300px"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {sharedData.include_forwards && (
            <TabsContent value="forwards" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Forward Attempts</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {forwards.length === 0 ? (
                    <div className="p-8 text-center text-sm text-muted-foreground">
                      No forwards yet
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-8"></TableHead>
                          <TableHead>Target URL</TableHead>
                          <TableHead>Method</TableHead>
                          <TableHead>Attempted</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Duration</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {forwards.map((forward) => (
                          <ForwardRow key={forward.id} forward={forward} />
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
        );
      })()}
      {/* Password Dialog */}
      <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              <DialogTitle>Password Required</DialogTitle>
            </div>
            <DialogDescription>
              This shared request is password-protected. Enter the password to view it.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && password) {
                    handlePasswordSubmit();
                  }
                }}
                autoFocus
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={handlePasswordSubmit}
              disabled={!password || isAuthenticating}
            >
              {isAuthenticating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Floating Branding Widget */}
      <a
        href="https://hooklistener.com"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 flex items-center gap-3 bg-background border-2 border-border rounded-full px-5 py-3 shadow-lg hover:shadow-xl transition-all hover:scale-105 group"
      >
        <div className="bg-slate-900 dark:bg-slate-800 rounded-full p-1.5">
          <img src="/logo.svg" alt="Hooklistener" className="h-5 w-5" />
        </div>
        <span className="font-semibold text-sm group-hover:text-primary transition-colors">
          Hooklistener
        </span>
      </a>
    </>
  );
};

export default SharedRequestView;
